import { Injectable, NgZone } from '@angular/core';
import { HttpInterceptorService } from '@ispm/core/services/http-interceptor.service';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { select, Store } from '@ngrx/store';
import { FORM_ACTIONS } from '../store/form.actions';
import { Observable } from 'rxjs/Observable';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import * as immutable from 'immutable';
import { DynamicFormModalResponse } from '@ispm/dynamic-form/classes/dynamic-form-modal-response.class';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { PlaceholderModalLoaderPayload } from '@ispm/layout/classes/placeholder-modal-loader-payload';
import { DynamicFormModalComponent } from '@ispm/dynamic-form/components/dynamic-form-modal/dynamic-form-modal.component';
import { DynamicFormModalPayload } from '@ispm/dynamic-form/classes/dynamic-form-modal-payload.class';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import {
  take,
  pluck,
  map,
  bufferTime,
  share,
  filter,
  retry,
  tap,
  flatMap,
  catchError, first
} from 'rxjs/operators';
import { DynamicFormItem } from '@ispm/dynamic-form/classes/dynamic-form-item.class';
import { IStepConfiguration } from '../../../configuration/interfaces/step-configuration.interface';
import { IConfigurationsMap } from '../../../configuration/interfaces/definition.interface';
import { get, isEmpty,  isEqual, Dictionary } from 'lodash';
import {IspmHttpClient} from "@ispm/core/services/ispm-http-client";
import {BehaviorSubject, EMPTY, Subject, throwError} from "rxjs";
import {selectFormConfiguration, selectFormControls} from "@ispm/dynamic-form/store/form.selectors";
import {AgregationService} from "@ispm-client/modules/aggregator/services/aggregator.service";
import {IdataBinding} from "@ispm-client/modules/aggregator/interfaces";
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import {DynamicFormComponent} from "@ispm/dynamic-form/dynamic-form.component";
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { FormGroup } from "@angular/forms";

@Injectable()
export class FormHandlerService {
  public static readonly FORM_CONFIG_KEY: string = `__FORM_CONFIG`;
  public static readonly ROOT_PROPERTY_PATTERN: RegExp = new RegExp('(^#(.*))');
  public static readonly FORM_ROUTE: string = 'definition/form/bundle';
  public static readonly FORM_RELOAD_ROUTE: string = 'definition/trigger/reload/Form';
  public static readonly FORM_DATA_ROUTE: string = '/formdata';

  private static readonly BUFFER_TIME = 200;

  public _changedFieldsQueue: Map<string, Observable<any>> = new Map<string, Observable<any>>();
  private _querySubject: Subject<string> = new Subject<string>();
  private requestSet = new Map();
  private responseSet = new Map();
  private _accordionSelectionChanged$ = new BehaviorSubject<{itemId: string, open: boolean}>(null);

  constructor(private _http: HttpInterceptorService,
              private _ispmHttp: IspmHttpClient,
              private store: Store<IgenomicsState>,
              private _zone: NgZone,
              private notificationMessageService: NotificationMessageService,
              private notificationMessage: NOTIFICATION_MESSAGE,
              private _modalLoader: DynamicModalLoaderService,
              private _aggregatorService: AgregationService) {
    this._zone.runOutsideAngular(()=>{
      this._querySubject.asObservable().pipe(
        bufferTime(FormHandlerService.BUFFER_TIME),
        filter(arr => arr.length > 0),
        flatMap(this.queryForms.bind(this))
      ).subscribe(() => {
        // PASS
        setTimeout(() => {
          _zone.run(() => {
          }); // To make sure automation doesnt get struck in consecutive
        });

      });
    });
  }

  /**
   * Build the form overrides which will pass to dynamic form as an input
   *
   * @param stepConfig
   * @param relevantForms - the forms to get the overrides for - default is all
   */
  public static BuildOverrides(stepConfig: IStepConfiguration<IConfigurationsMap>, ...relevantForms: string[]):
    { [key: string]: Partial<DynamicFormItem> } {
    const formOverrides = { };
    if (!get(stepConfig, 'fieldsValidation.override', null)) {
      return formOverrides;
    }
    stepConfig.fieldsValidation.override.forEach(override => {
      if (relevantForms.length > 0 &&  relevantForms.includes(override.form))  { return; }

      if (!(override.form in formOverrides)) {
        formOverrides[override.form] = { };
      }
      const field = override.field || this.FORM_CONFIG_KEY;
      formOverrides[override.form][field] = override;
    });

    return formOverrides;
  }

  static IsRootProperty(property: string): boolean {
    return !property || this.ROOT_PROPERTY_PATTERN.test(property);
  }

  public getStoredFormConfigs(): Observable<immutable.Map<string, IdynamicFormItem[]>> {
    return this.store.select('forms', 'configs');
  }

  public getFormDataBindings(formId: string): Observable<IdataBinding[]> {
    return this.store.select('forms', 'dataBindings').take(1).map(forms => forms.get(formId));
  }

  public getStoredFormById(formId: string) {
    return this.getStoredFormConfigs().pipe(
      map(forms => forms.get(formId))
    );
  }

  public getStoredReflectsByFormId(formId: string){
    return this.store.select('forms', 'formConfigurations').pipe(
      filter(forms => forms.has(formId) && forms.get(formId)!= undefined && forms.get(formId)!= null),
      map(forms => forms.get(formId)),
      map(form => form.reflects ? form.reflects : [])
    );
  }


  public getStoredRootConfigByFormId(formId: string){
    return this.store.select('forms', 'formConfigurations').pipe(
      filter(forms => forms.has(formId) && forms.get(formId)!= undefined && forms.get(formId)!= null),
      map((forms) => forms.get(formId))
    );
  }

  public getForm(formId: string, forceFormFetch?:boolean) {
    if((!formId)||(formId.startsWith(DynamicFormComponent.GENERATED_FORM_ID_PREFIX))){
      return EMPTY;
    }

    if (this.requestSet.has(formId) && !forceFormFetch) {
      return this.requestSet.get(formId);
    } else {
      const storeListener = this.getFormFromStore(formId);
      if(!this.responseSet.get(formId)){
        this._querySubject.next(formId);
      }
      this.requestSet.set(formId, storeListener);
      return storeListener;
    }
  }

  private queryForms(formIds: string[]){
    return this._ispmHttp.post(`${FormHandlerService.FORM_ROUTE}`,{
      resource: 'Form',
      ids: formIds
    }).pipe(
      retry(2),
      tap((forms)=> {
        this.addFormstoRequestSet(forms);
      }),
      share()
    );
  }

  private getFormFromStore(formId: string){
    return this.store.pipe(select(selectFormConfiguration(formId)), filter( form => !!form),take(1))
  }

  public addFormstoRequestSet(forms){
    forms.map(form => {
      if(form) {
        this.responseSet.set(form.id,true);
        // this.addForm(form.id, form.inputs, form.reflects, form.dataBinding, form.configBindings,form);
      }
    });
    this.store.dispatch({
      type: FORM_ACTIONS.ADD_FORMS,
      payload: forms
    });
  }

  public addForm(name, inputs, reflects?, dataBinding?, configBindings?, form?, addtoRequestSet = false) {

    if (!name) {
      return;
    }
    this.store.dispatch({
      type: FORM_ACTIONS.ADD_FORM,
      payload: {
        name,
        inputs
      }
    });
    this.addFormProperty(name, form); //TODO Would enhance formConfiguration to hold all data against formId
    this.addDataBindings(name, dataBinding);
    this.addConfigBindings(name, configBindings);
    if(addtoRequestSet){
      this.responseSet.set(form.id,true);
    }
  }

  public updateForm(name, inputs){
    this.store.dispatch({
      type: FORM_ACTIONS.ADD_FORM,
      payload: {
        name,
        inputs
      }
    });
  }

  public removeForm(name) {
    if (!name) {
      return
    }
    this.store.dispatch({
      type: FORM_ACTIONS.REMOVE_FORM,
      payload: {
        name
      }
    });
    this.removeReflects(name);
    this.removeDataBindings(name);
    this.removeConfigBindings(name);
  }

  private addFormProperty(name: string ,formProps: any) {
    if (formProps) {
      this.store.dispatch({
        type: FORM_ACTIONS.ADD_FORM_PROPS,
        payload: {
          name,
          form:{...formProps}
        }
      });
    }
  }

  public addDataBindings(name: string, dataBindings) {
    this.store.dispatch({
      type: FORM_ACTIONS.ADD_DATABINDINGS,
      payload: {
        name,
        dataBindings: dataBindings ? dataBindings : null
      }
    });
    if(!(isEmpty(dataBindings))) {
      this._aggregatorService.registerIndepedantDatabindings(dataBindings);
    }
  }
  public addConfigBindings(name: string, configBindings) {
    if (configBindings !== undefined) {
      this.store.dispatch({
        type: FORM_ACTIONS.ADD_CONFIG_BINDINGS,
        payload: {
          name,
          configBindings
        }
      });
    }
  }

  public removeReflects(name: string) {
    this.store.dispatch({
      type: FORM_ACTIONS.REMOVE_FORM_PROPS,
      payload: {
        name
      }
    });
  }
  public removeDataBindings(name: string) {
    this.store.dispatch({
      type: FORM_ACTIONS.REMOVE_DATABINDINGS,
      payload: {
        name
      }
    });

  }
  public removeConfigBindings(name: string) {
    this.store.dispatch({
      type: FORM_ACTIONS.REMOVE_CONFIG_BINDINGS,
      payload: {
        name
      }
    });
  }
  public getChangedFields(contextQuery: string) {
    if (!contextQuery) { return Observable.empty(); }
    if (this._changedFieldsQueue.has(contextQuery)) {
      return this._changedFieldsQueue.get(contextQuery);
    }
    const query = this._http.get(`${FormHandlerService.FORM_DATA_ROUTE}/changedFields`, { contextQuery}, false)
      .map(data => data.json())
      .share()
      .finally(() => {
        this._changedFieldsQueue.delete(contextQuery);
      })
      .catch(() => []);
    this._changedFieldsQueue.set(contextQuery, query);
    return query;
  }

  public setChangedFields(contextQuery: Array<{ reference: string, resourceType: string}>, fields: Array<string>) {
    if (!(contextQuery && fields && fields.length > 0)) { return Observable.empty(); }
    return this._http.put(`${FormHandlerService.FORM_DATA_ROUTE}/changedFields`, { contextQuery, fields}, false)
      .retry(2)
      .map(data => data.json())
      .catch(() => fields);
  }

  public reloadFormConfiguration(): Observable<any>{
    return this._ispmHttp.get(FormHandlerService.FORM_RELOAD_ROUTE)
      .pipe(
        tap(_ =>{
          this.notificationMessageService.success(this.notificationMessage.RELOAD_OF_FORM_DEFINITIONS_TRIGGERED_SUCCESSFULLY);
        }),
        catchError(err => {
          console.error(err);
          this.notificationMessageService.error(this.notificationMessage.FAILED_TO_TRIGGER_RELOAD_OF_FORM_DEFINITIONS);
          return throwError(err);
        })
      )
  }

  popupDynamicFormModal(
    title: string,
    formIdOrConfig: string | DynamicFormItem[],
    formData: any,
    approveBtn?: string,
    declineBtn?: string,
    additionalPayload?: Partial<DynamicFormModalPayload>,
    channel?: string,
    componentToLoad?: any,
    backdropAllowed: boolean = true,
    disabledFields?: Array<string>,
    note?: string,
    formEditMode: boolean = false,
    modalEditMode: boolean = true
  ): Observable<DynamicFormModalResponse> {

    const payload = DynamicFormModalPayload.builder()
      .setFormData(formData)
      .setDisabledFields(disabledFields)
      .setPopupMode(true)
      .setDisabled(false)
      .setEditMode(formEditMode)
      .setNote(note);
    if (typeof formIdOrConfig === 'string') {
      payload.setFormId(formIdOrConfig as string);
    } else {
      payload.setFormConfig(formIdOrConfig as DynamicFormItem[]);
    }

    Object.assign(payload, additionalPayload);

    return this._modalLoader.popupModal(
        PlaceholderModalComponent,
        PlaceholderModalLoaderPayload.builder()
          .setComponentToLoad(componentToLoad ? componentToLoad : DynamicFormModalComponent)
          .setTitle(title)
          .setApproveButton(approveBtn)
          .setDeclineButton(declineBtn)
          .setPayload(payload)
          .setCssClasses(payload.classes)
          .setBackdropAllowed(backdropAllowed)
          .setEditMode(modalEditMode),
        ModalTypes.CUSTOM,
        channel
      ).pipe(pluck('payload'));
  }

  getChangedFieldsFromObject(prev: any, curr: any, formConfig?:Dictionary<IdynamicFormItem>) {
    const changedFields = new Set();
    Array.from(new Set(Object.keys(formConfig))).forEach(field => {
      if (formConfig[field]["ignoreDiff"] ) {
        return;
      }

      const currValue = get(curr, field);
      const prevValue = get(prev, field);

      if (typeof currValue === 'object' && JSON.stringify(currValue) !== JSON.stringify(prevValue)) {
        changedFields.add(field);
      } else if (currValue !== prevValue) {
        changedFields.add(field);
      }
    });
    return Array.from(changedFields);
  }

  defaultFormValueCheck(formData,formId:string):boolean{
    let formConfig:IdynamicFormItem[];
    if(formId){
      return this.getForm(formId).subscribe(data=>{
        formConfig=data.inputs;
        let flag=false;
        formConfig.map((formItems)=>{
          if(formData[formItems.name]) {
            if (formItems.type.toLowerCase() === "datepicker") {
              if(formItems.defaultValue === "now"){
                const today: any = new Date();
                if (formData[formItems.name] !== today.toPlatformFormat())
                  flag = true;
              }
              else
             flag=true;
            } else if (typeof (formData[formItems.name]) === "object" && !isEqual(formItems.defaultValue, formData[formItems.name]))
              flag = true;
            else if (typeof (formData[formItems.name]) !== "object" && (formItems.type.toLowerCase() !== "datepicker") && formItems.defaultValue !== formData[formItems.name])
              flag = true;
          }
        });
        return flag;
      })
    }
  }

  closeDynamicFormModal(name: string): void {
    this._modalLoader.dismissObservable(name);
  }

  //Allows to subscribe to this event and get notified when user selects a different accordion tab
  get accordionSelectionChanged$(): BehaviorSubject<{itemId: string, open: boolean}> {
    return this._accordionSelectionChanged$;
  }

  public updateFormControls(formId: string, form: FormGroup): void {
    let controls: any[] = [];
    Object.keys(form.controls).forEach(key => {
      controls.push({
        name: key,
        errors: form.controls[key].errors,
        status: form.controls[key].status,
        value: form.controls[key].value,
        pristine: form.controls[key].pristine,
        touched: form.controls[key].touched
      });
    });
    this.store.dispatch({
      type: FORM_ACTIONS.UPDATE_FORM_CONTROLS,
      payload: {
        name: formId,
        controls,
        formStatus: form?.status
      }
    });
  }

  public getFormControls(formId: string){
    return this.store.pipe(select(selectFormControls(formId)), first());
  }
}
